/**
 * DepositFormDialog — Create/Edit Forward Deposit (forward / top-up / repayment).
 * Dùng cho ForwardListPage (top-level Forward P) + Tab Forward Deposits của LocalP detail.
 *
 * Workflow:
 *  - Khi user chọn contract_no (autocomplete), gọi useGetLocalPQuery để lấy detail
 *    và auto-suggest: quantity, fx, price, price_unit + display contract_date,
 *    deposit_repayment_date, supplier_name, interest rate.
 *  - Amount = quantity × price × fx tự cập nhật (user có thể override).
 */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { useSnackbar } from '@/hooks/useSnackbar'
import { useGetLocalPQuery, useListLocalPQuery } from '@/store/api/localPApi'
import {
  useCreateForwardMutation,
  useUpdateForwardMutation,
} from '@/store/api/localPForwardsApi'
import {
  useListForwardTypesQuery,
  useListPriceUnitsQuery,
} from '@/store/api/lookupsApi'
import type {
  ForwardType,
  LocalPForwardCreateIn,
  LocalPForwardOut,
} from '@/types/api'
import { formatDate } from '@/utils/date'
import { getErrorMessage } from '@/utils/error'
import { formatNumber } from '@/utils/money'

export interface DepositFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  /** Khi mở từ contract detail thì lock contract_no + ẩn autocomplete. */
  fixedContractNo?: string
  initialValue?: LocalPForwardOut | null
  onClose: () => void
  onSaved?: (forwardId: number) => void
}

interface DepositFormValues {
  contract_no: string
  forward_type: ForwardType | ''
  payment_date: string
  price: number | ''
  market_closing_price: number | ''
  fx: number | ''
  quantity: number | ''
  amount: number | ''
  amount_override: boolean
  price_unit: string
  note: string
}

const EMPTY: DepositFormValues = {
  contract_no: '',
  forward_type: '',
  payment_date: dayjs().format('YYYY-MM-DD'),
  price: '',
  market_closing_price: '',
  fx: '',
  quantity: '',
  amount: '',
  amount_override: false,
  price_unit: '',
  note: '',
}

const oneYearFromNow = () => dayjs().add(1, 'year').format('YYYY-MM-DD')

const schema: yup.ObjectSchema<DepositFormValues> = yup.object({
  contract_no: yup.string().trim().required('Bắt buộc chọn hợp đồng'),
  forward_type: yup
    .mixed<ForwardType | ''>()
    .oneOf(['First deposit', 'Top up', 'Repayment', ''] as const)
    .test('required', 'Bắt buộc chọn loại deposit', (v) => v !== '' && !!v)
    .required(),
  payment_date: yup
    .string()
    .required('Bắt buộc chọn ngày thanh toán')
    .test('not-too-future', 'Ngày thanh toán không quá xa tương lai (>1 năm)', (v) => {
      if (!v) return false
      return v <= oneYearFromNow()
    }),
  price: yup.mixed<number | ''>().default(''),
  market_closing_price: yup.mixed<number | ''>().default(''),
  fx: yup
    .mixed<number | ''>()
    .test('required', 'Bắt buộc nhập FX', (v) => v !== '' && v !== undefined && v !== null)
    .required(),
  quantity: yup
    .mixed<number | ''>()
    .test('required', 'Bắt buộc nhập số lượng', (v) => v !== '' && v !== undefined && v !== null)
    .test('positive', 'Số lượng phải > 0', (v) => typeof v === 'number' && v > 0)
    .required(),
  amount: yup
    .mixed<number | ''>()
    .test('positive', 'Amount phải > 0', (v) => typeof v === 'number' && v > 0)
    .required(),
  amount_override: yup.boolean().default(false),
  price_unit: yup.string().trim().default(''),
  note: yup.string().trim().default(''),
}) as yup.ObjectSchema<DepositFormValues>

function fromForward(f: LocalPForwardOut): DepositFormValues {
  const n = (x: number | null | undefined): number | '' =>
    x === null || x === undefined ? '' : x
  const s = (x: string | null | undefined): string => x ?? ''
  return {
    contract_no: s(f.contract_no),
    forward_type: (f.forward_type as ForwardType) ?? '',
    payment_date: s(f.payment_date),
    price: n(f.price),
    market_closing_price: n(f.market_closing_price),
    fx: n(f.fx),
    quantity: n(f.quantity),
    amount: n(f.amount),
    amount_override: true, // edit mode: respect saved value
    price_unit: s(f.price_unit),
    note: s(f.note),
  }
}

function toApi(v: DepositFormValues): LocalPForwardCreateIn {
  const num = (x: number | ''): number | null => (x === '' ? null : x)
  const str = (x: string): string | null => (x.trim() === '' ? null : x.trim())
  return {
    contract_no: v.contract_no.trim(),
    forward_type: v.forward_type as ForwardType,
    payment_date: v.payment_date,
    price: num(v.price),
    market_closing_price: num(v.market_closing_price),
    fx: num(v.fx),
    quantity: num(v.quantity),
    amount: num(v.amount),
    price_unit: str(v.price_unit),
    note: str(v.note),
  }
}

export function DepositFormDialog({
  open,
  mode,
  fixedContractNo,
  initialValue,
  onClose,
  onSaved,
}: DepositFormDialogProps) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createForward, { isLoading: creating }] = useCreateForwardMutation()
  const [updateForward, { isLoading: updating }] = useUpdateForwardMutation()
  const loading = creating || updating

  const { data: forwardTypes = [] } = useListForwardTypesQuery()
  const { data: priceUnits = [] } = useListPriceUnitsQuery()

  // Lookup contracts cho autocomplete (filter Differential + active)
  const { data: contractsList } = useListLocalPQuery({
    contract_type: 'Differential',
    is_status: 1,
    page: 1,
    page_size: 200,
  })
  const contractOptions = useMemo(() => contractsList?.items ?? [], [contractsList])

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<DepositFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY,
  })

  const selectedContractNo = watch('contract_no')
  const quantity = watch('quantity')
  const price = watch('price')
  const fx = watch('fx')
  const amountOverride = watch('amount_override')

  // Lazy load detail of selected contract for auto-suggest
  const { data: contractDetailRes } = useGetLocalPQuery(selectedContractNo, {
    skip: !selectedContractNo,
  })
  const contractDetail = contractDetailRes?.data

  // Track previously seen contract_no to only auto-fill once (avoid stomping user edits)
  const [lastFilledContractNo, setLastFilledContractNo] = useState<string>('')

  useEffect(() => {
    if (!open) return
    setLastFilledContractNo('')
    if (isEdit && initialValue) {
      reset(fromForward(initialValue))
    } else {
      reset({
        ...EMPTY,
        contract_no: fixedContractNo ?? '',
      })
    }
  }, [open, isEdit, initialValue, fixedContractNo, reset])

  // Auto-suggest fields when contract changes (create-mode only)
  useEffect(() => {
    if (isEdit) return
    if (!contractDetail) return
    if (!selectedContractNo) return
    if (lastFilledContractNo === selectedContractNo) return

    setValue('quantity', contractDetail.quantity_kg ?? '')
    setValue('fx', contractDetail.fx ?? '')
    setValue('price_unit', contractDetail.price_unit ?? '')
    const suggestedPrice =
      contractDetail.outright_price ?? contractDetail.ptbf_diffs ?? ''
    setValue('price', suggestedPrice as number | '')
    setLastFilledContractNo(selectedContractNo)
  }, [
    isEdit,
    contractDetail,
    selectedContractNo,
    lastFilledContractNo,
    setValue,
  ])

  // Auto-compute amount = quantity × price × fx (unless user override)
  useEffect(() => {
    if (amountOverride) return
    const q = typeof quantity === 'number' ? quantity : 0
    const p = typeof price === 'number' ? price : 0
    const f = typeof fx === 'number' ? fx : 0
    if (q > 0 && p > 0 && f > 0) {
      setValue('amount', Number((q * p * f).toFixed(0)))
    }
  }, [quantity, price, fx, amountOverride, setValue])

  const interestRatePct = useMemo(() => {
    const v = contractDetail?.margin_interest_month
    return typeof v === 'number' ? v * 100 : null
  }, [contractDetail])

  const onSubmit = handleSubmit(
    async (values) => {
      const payload = toApi(values)
      try {
        if (isEdit && initialValue) {
          const res = await updateForward({
            forwardId: initialValue.id,
            body: payload,
          }).unwrap()
          snack.success('Đã cập nhật deposit')
          onSaved?.(res.data?.id ?? initialValue.id)
        } else {
          const res = await createForward(payload).unwrap()
          snack.success('Đã thêm deposit')
          onSaved?.(res.data?.id ?? 0)
        }
        onClose()
      } catch (err) {
        snack.error(
          getErrorMessage(err, isEdit ? 'Cập nhật deposit thất bại' : 'Thêm deposit thất bại'),
        )
      }
    },
    () => {
      snack.error('Vui lòng kiểm tra lại các trường còn lỗi')
    },
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? 'Sửa Deposit' : 'Thêm Deposit (Forward / Top-up / Repayment)'}
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {/* Row 1: Contract autocomplete (or fixed display) + Payment Date */}
          <Box display="flex" gap={2} flexWrap="wrap">
            {fixedContractNo ? (
              <TextField
                label="Hợp đồng"
                size="small"
                value={fixedContractNo}
                disabled
                sx={{ flex: '1 1 240px' }}
              />
            ) : (
              <Controller
                control={control}
                name="contract_no"
                render={({ field }) => {
                  const found = contractOptions.find((c) => c.contract_no === field.value)
                  // Fallback shim cho edit mode khi contract chưa nằm trong list (page_size=200).
                  const selected =
                    found ??
                    (field.value
                      ? ({
                          contract_no: field.value,
                          supplier_name: null,
                          cp_code: null,
                        } as (typeof contractOptions)[number])
                      : null)
                  return (
                    <Autocomplete
                      size="small"
                      sx={{ flex: '2 1 320px' }}
                      options={contractOptions}
                      value={selected}
                      onChange={(_e, v) => field.onChange(v?.contract_no ?? '')}
                      getOptionLabel={(opt) =>
                        opt
                          ? `${opt.contract_no} — ${opt.supplier_name ?? opt.cp_code ?? ''}`
                          : ''
                      }
                      isOptionEqualToValue={(o, v) => o.contract_no === v.contract_no}
                      disabled={isEdit}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Hợp đồng (Differential active) *"
                          error={!!errors.contract_no}
                          helperText={errors.contract_no?.message}
                        />
                      )}
                    />
                  )
                }}
              />
            )}
            <Controller
              control={control}
              name="payment_date"
              render={({ field }) => (
                <DatePicker
                  label="Ngày thanh toán *"
                  format="DD/MM/YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { flex: '1 1 180px' },
                      error: !!errors.payment_date,
                      helperText: errors.payment_date?.message,
                    },
                  }}
                />
              )}
            />
          </Box>

          {/* Row 2: Display-only contract context */}
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Ngày HĐ"
              size="small"
              value={formatDate(contractDetail?.contract_date) || ''}
              disabled
              sx={{ flex: '1 1 160px' }}
            />
            <TextField
              label="Ngày tất toán deposit"
              size="small"
              value={formatDate(contractDetail?.deposit_repayment_date) || ''}
              disabled
              sx={{ flex: '1 1 180px' }}
            />
            <TextField
              label="Lãi suất (%/tháng)"
              size="small"
              value={interestRatePct === null ? '' : formatNumber(interestRatePct, 2)}
              disabled
              sx={{ flex: '1 1 160px' }}
            />
            <TextField
              label="Nhà cung cấp"
              size="small"
              value={contractDetail?.supplier_name ?? contractDetail?.short_name ?? ''}
              disabled
              sx={{ flex: '2 1 280px' }}
            />
          </Box>

          {/* Row 3: Forward type + Price + Market Closing */}
          <Box display="flex" gap={2} flexWrap="wrap">
            <Controller
              control={control}
              name="forward_type"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Loại Deposit *"
                  select
                  size="small"
                  sx={{ flex: '1 1 200px' }}
                  error={!!errors.forward_type}
                  helperText={errors.forward_type?.message}
                >
                  <MenuItem value="">— Chưa chọn —</MenuItem>
                  {forwardTypes.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              control={control}
              name="price"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Giá"
                  type="number"
                  size="small"
                  sx={{ flex: '1 1 160px' }}
                  error={!!errors.price}
                  helperText={errors.price?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="market_closing_price"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Market Closing Price"
                  type="number"
                  size="small"
                  sx={{ flex: '1 1 200px' }}
                  error={!!errors.market_closing_price}
                  helperText={errors.market_closing_price?.message}
                />
              )}
            />
          </Box>

          {/* Row 4: FX + Quantity + Price unit */}
          <Box display="flex" gap={2} flexWrap="wrap">
            <Controller
              control={control}
              name="fx"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="FX *"
                  type="number"
                  size="small"
                  sx={{ flex: '1 1 140px' }}
                  error={!!errors.fx}
                  helperText={errors.fx?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="quantity"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Số lượng (kg) *"
                  type="number"
                  size="small"
                  sx={{ flex: '1 1 160px' }}
                  error={!!errors.quantity}
                  helperText={errors.quantity?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="price_unit"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Đơn vị giá"
                  select
                  size="small"
                  sx={{ flex: '1 1 180px' }}
                  error={!!errors.price_unit}
                  helperText={errors.price_unit?.message}
                >
                  <MenuItem value="">— Chưa chọn —</MenuItem>
                  {priceUnits.map((u) => (
                    <MenuItem key={u.value} value={u.value}>
                      {u.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>

          {/* Row 5: Amount + override toggle */}
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Controller
              control={control}
              name="amount"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Amount (VND)"
                  type="number"
                  size="small"
                  disabled={!amountOverride}
                  sx={{ flex: '1 1 240px' }}
                  error={!!errors.amount}
                  helperText={
                    errors.amount?.message ?? 'Mặc định = quantity × price × fx'
                  }
                />
              )}
            />
            <Controller
              control={control}
              name="amount_override"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Tự nhập Amount"
                />
              )}
            />
          </Box>

          {/* Row 6: Note */}
          <Controller
            control={control}
            name="note"
            render={({ field }) => (
              <TextField
                {...field}
                label="Ghi chú"
                size="small"
                multiline
                minRows={2}
                error={!!errors.note}
                helperText={errors.note?.message}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Huỷ
        </Button>
        <Button
          onClick={() => void onSubmit()}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Lưu
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DepositFormDialog
